// Dependencies
import { useLocation } from "react-router";

// Material
import Breadcrumbs from "@mui/material/Breadcrumbs";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useBreadcrumbLabels } from "@/contexts/BreadcrumbLabelContext";
import LinkBehavior from "@/components/LinkBehavior";

// Convert string to Title format
export function toTitleCase(str: string) {
    return str?.replace(/\b\w+/g, function (s) {
        return s.charAt(0).toUpperCase() + s.substr(1).toLowerCase();
    });
}

// note: if you want more friendly names in the breadcrumbs,
// see breadcrumbNameMap in https://mui.com/components/breadcrumbs/#integration-with-react-router

interface BreadcrumbCommonProps {
    overrides?: Record<string, string>;
    linkOverrides?: Record<string, string>;
}

function isLikelyEntityId(segment: string) {
    // Matches canonical UUIDs and compact 32-char hex IDs.
    return (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
        segment,
    ) || /^[0-9a-f]{32}$/i.test(segment));
}

function BreadcrumbCommon({ overrides, linkOverrides }: BreadcrumbCommonProps) {
    const { dynamicLabels } = useBreadcrumbLabels();
    let { pathname } = useLocation();
    let pathnames = pathname.split("/").filter((x) => x);
    // optional: remove numbered paths (resource id's)
    // note: remove/change below as per app requirements
    // pathnames = pathnames.filter(x => isNaN(x));

    const hasUnresolvedEntitySegment = pathnames.some((value, index) => {
        if (!isLikelyEntityId(value)) return false;

        const to = `/${pathnames.slice(0, index + 1).join("/")}`;
        return !dynamicLabels[to];
    });

    if (hasUnresolvedEntitySegment) {
        return null;
    }

    return (
        <Stack spacing={2}>
            <Breadcrumbs
                aria-label="Breadcrumb"
                separator={<NavigateNextIcon fontSize="small" />}
            >
                <LinkBehavior color="inherit" href={linkOverrides?.["/"] ?? "/"}>
                    Home
                </LinkBehavior>

                {/* Renders path segments */}
                {pathnames.map((value, index) => {
                    const last = index === pathnames.length - 1;
                    const to = `/${pathnames.slice(0, index + 1).join("/")}`;
                    const title =
                        dynamicLabels[to] ??
                        overrides?.[to] ??
                        overrides?.[value] ??
                        toTitleCase(value);
                    const linkTo = linkOverrides?.[to] ?? linkOverrides?.[value] ?? to;

                    return last ? (
                        <Typography key={to} color="textPrimary">
                            {title}
                        </Typography>
                    ) : (
                        <LinkBehavior key={to} color="inherit" href={linkTo}>
                            {title}
                        </LinkBehavior>
                    );
                })}
            </Breadcrumbs>
        </Stack>
    );
}

export { BreadcrumbCommon };
